import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { LoginComponent } from '@security/login/login.component';
import { LogoutComponent } from '@security/logout/logout.component';

import { AuthGuard } from '@auth0/auth0-angular';
import {
	AccountAdminGuard,
	AdminGuard,
	FeatureGuard
} from '@services/authentication.service';
import { AccountFeatures } from '@services/accounts.service';

import { HomeNavigationComponent } from '@layout/home-navigation/home-navigation.component';
import { HomeDefaultPageComponent } from '@layout/home-default-page/home-default-page.component';

import { ProfileManageComponent } from '@admin/users/profile-manage/profile-manage.component';
import { ProfileComponent } from '@admin/users/profile/profile.component';
import { AccountManageComponent } from '@admin/accounts/account-manage/account-manage.component';
import { AccountInviteComponent } from '@admin/accounts/account-invite/account-invite.component';
import { AccountManageFeaturesComponent } from '@admin/accounts/account-manage-features/account-manage-features.component';

import { SettingsComponent } from '@components/site-settings/settings.component';
import { HelpComponent } from '@components/help/help.component';
import { NotificationsComponent } from '@components/notifications/notifications.component';
import { RoutingComponent } from '@components/routing/routing.component';
import { RouteFolderComponent } from '@components/route-folder/route-folder.component';
import { RedirectComponent } from '@components/redirect/redirect.component';

import { EventsComponent } from '@components/events/events/events.component';
import { EventDetailsComponent } from '@components/events/event-details/event-details.component';

import { RulesComponent } from '@components/rules/rules/rules.component';
import { RuleDetailsComponent } from '@components/rules/rule-details/rule-details.component';

import { ActionListComponent } from '@components/actions/action-list/action-list.component';
import { ActionDetailsComponent } from '@components/actions/action-details/action-details.component';

import { ModelsComponent } from '@components/models/models/models.component';
import { ModelDetailsComponent } from '@components/models/model-details/model-details.component';

import { WatchListComponent } from '@components/watch-list/watch-list.component';
import { UserFavouritesComponent } from '@components/user-favourites/user-favourites.component';

import { AssetListComponent } from './organisation-assets/asset-list/asset-list.component';
import { AssetDetailsComponent } from './organisation-assets/asset-details/asset-details.component';
import { AssetComparisonComponent } from './organisation-assets/asset-comparison/asset-comparison.component';

import { OrganisationFlaggedAssetsComponent } from '@components/organisation-flagged-assets/organisation-flagged-assets.component';

import { SAPCaseListComponent } from './cmms/sap-case-list/sap-case-list.component';
import { SAPCaseDetailComponent } from './cmms/sap-case-detail/sap-case-detail.component';

import { EnergyDashboardComponent } from './organisations/energy-dashboard/energy-dashboard.component';
import { EnergyReportingComponent } from '@components/energy-reporting/energy-reporting.component';
import { HistoricalReportsComponent } from '@components/historical-reports/historical-reports.component';

import { HierarchyDashboardComponent } from './organisations/hierarchy/hierarchy-dashboard/hierarchy-dashboard.component';
import { HeirarchyManagementComponent } from './organisations/hierarchy/management/heirarchy-management/heirarchy-management.component';
import { OrganisationDataModelComponent } from './organisations/hierarchy/management/organisation-data-model/organisation-data-model.component';

import { DashboardCreationComponent } from './organisations/dashboard/dashboard-creation/dashboard-creation.component';
import { CustomDashboardComponent } from './organisations/dashboard/custom-dashboard/custom-dashboard.component';

import { DataIntegrationsComponent } from './integrations/data-integrations/data-integrations.component';
import { DataIntegrationsDetailComponent } from './integrations/data-integrations-detail/data-integrations-detail.component';

import { VirtualSensorsComponent } from '@components/virtual-sensors/virtual-sensors.component';
import { VirtualSensorDetailsComponent } from '@components/virtual-sensor-details/virtual-sensor-details.component';

import { AccountdisabledComponent } from '@admin/users/accountdisabled/accountdisabled.component';
import { UnverifiedEmailComponent } from '@admin/users/unverified-email/unverified-email.component';
import { UnauthorisedComponent } from '@admin/users/unauthorised/unauthorised.component';
import { ErrorComponent } from './error/error.component';
import { AccountOrganisationGuard } from './guards/account-organisation-guard';

/**
 * Build the master root pages.
 */
const routes: Routes = [

	 /*
	 * ============================================================
	 * PUBLIC / HOME
	 * ============================================================
	 */
	{
		path: '',
		component: HomeNavigationComponent,

		children: [

			{
				path: '',
				component: HomeDefaultPageComponent
			},

			/*
			* ============================================================
			* AUTHENTICATED APPLICATION
			* ============================================================
			*
			* Everything underneath HomeNavigation requires authentication.
			*/
			{
				path: '',
				canActivateChild: [AuthGuard],
				children: [
					/*
					* --------------------------------------------------------
					* USER MANAGEMENT
					* --------------------------------------------------------
					*/
					{
						path: 'users-management',
						component: ProfileManageComponent,
						canActivate: [AdminGuard],

						children: [
							{
								path: ':userGUID',
								component: ProfileComponent
							}
						]
					},


					/*
					 * --------------------------------------------------------
					 * ACCOUNT MANAGEMENT
					 * --------------------------------------------------------
					 */
					{
						path: 'accounts-management/:accountID',
						component: AccountManageComponent
					},

					{
						path: 'feature-management',
						component: AccountManageFeaturesComponent,
						canActivate: [AdminGuard]
					},


					/*
					 * --------------------------------------------------------
					 * USER PAGES
					 * --------------------------------------------------------
					 */
					{
						path: 'profile',
						component: ProfileComponent
					},

					{
						path: 'settings',
						component: SettingsComponent
					},

					{
						path: 'help',
						component: HelpComponent
					},

					{
						path: 'notifications',
						component: NotificationsComponent
					},

					{
						path: 'invite',
						component: AccountInviteComponent
					},

					/*
					 * --------------------------------------------------------
					 * SETUP / ADMINISTRATION
					 * --------------------------------------------------------
					 */
					{
						path: 'setup',
						component: RouteFolderComponent,

						children: [

							{
								path: 'heirarchy-management/:organisation',
								component: HeirarchyManagementComponent,
								canActivate: [AccountAdminGuard]
							},

							{
								path: 'integration-management/:organisation',
								component: DataIntegrationsComponent,
								canActivate: [AccountAdminGuard],

								children: [
									{
										path: 'integration/:integrationID',
										component: DataIntegrationsDetailComponent,
										canActivate: [AccountAdminGuard]
									}
								]
							},

							{
								path: 'data-model-management/:organisation',
								component: OrganisationDataModelComponent,
								canActivate: [AccountAdminGuard]
							},

							{
								path: 'dashboard-management/:organisation',
								component: DashboardCreationComponent,
								canActivate: [AccountAdminGuard]
							},

							{
								path: 'virtual-datapoint-management/:organisation',
								component: VirtualSensorsComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Virtual_Sensors
								}
							},

							{
								path: 'virtual-datapoint-management/:organisation/virtual-datapoint/:virtualSensorID',
								component: VirtualSensorDetailsComponent
							}
						]
					},

					{
						path: 'routing',
						component: RoutingComponent
					},


					/*
					 * --------------------------------------------------------
					 * LEGACY ORGANISATION ROUTES
					 * --------------------------------------------------------
					 *
					 * These redirect old URLs to the new routing structure.
					 * Keep these before the dynamic organisation routes.
					 */
					{
						path: 'organisations/:organisation/dashboard',
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation/location/:sublocation/asset/:assetID',
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation/location/:sublocation',
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation/asset/:assetID',
						component: RedirectComponent
					},

					{
						path: 'organisations/:organisation',
						component: RedirectComponent
					},


					/*
					 * ========================================================
					 * ACCOUNT/ORGANISATION ROUTES
					 * ========================================================
					 *
					 * All account/organisation-specific routes share:
					 *
					 *     :account/organisation/:organisation
					 *
					 * CheckActiveRoute is therefore applied once to the
					 * organisation route group and protects every child.
					 */
					{
						path: ':account/organisation/:organisation',
						canActivateChild: [AccountOrganisationGuard],
						children: [

							/*
							 * ------------------------------------------------
							 * ORGANISATION HOME
							 * ------------------------------------------------
							 */
							{
								path: '',
								component: HomeDefaultPageComponent
							},

							{
								path: 'dashboard',
								component: CustomDashboardComponent
							},


							/*
							 * ------------------------------------------------
							 * EVENTS
							 * ------------------------------------------------
							 */
							{
								path: 'event',
								component: EventsComponent
							},

							{
								path: 'event/:eventID',
								component: EventDetailsComponent
							},


							/*
							 * ------------------------------------------------
							 * RULES
							 * ------------------------------------------------
							 */
							{
								path: 'rules',
								component: RulesComponent
							},

							{
								path: 'rules/:ruleID',
								component: RuleDetailsComponent
							},


							/*
							 * ------------------------------------------------
							 * ACTIONS
							 * ------------------------------------------------
							 */
							{
								path: 'actions',
								component: ActionListComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Actions
								}
							},

							{
								path: 'actions/:actionID',
								component: ActionDetailsComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Actions
								}
							},


							/*
							 * ------------------------------------------------
							 * WATCH LIST
							 * ------------------------------------------------
							 */
							{
								path: 'watch',
								component: AssetListComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Watch_List
								},

								children: [
									{
										path: ':assetID',
										component: WatchListComponent,
										canActivate: [FeatureGuard],
										data: {
											featureID: AccountFeatures.Watch_List
										}
									}
								]
							},


							/*
							 * ------------------------------------------------
							 * MODELS
							 * ------------------------------------------------
							 */
							{
								path: 'models',
								component: ModelsComponent
							},

							{
								path: 'models/:modelID',
								component: ModelDetailsComponent
							},


							/*
							 * ------------------------------------------------
							 * FAVOURITES
							 * ------------------------------------------------
							 */
							{
								path: 'favourites',
								component: UserFavouritesComponent
							},


							/*
							 * ------------------------------------------------
							 * ASSET COMPARISON
							 * ------------------------------------------------
							 */
							{
								path: 'compare',
								component: AssetComparisonComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Compare_Assets
								}
							},


							/*
							 * ------------------------------------------------
							 * SAP / CMMS
							 * ------------------------------------------------
							 */
							{
								path: 'SAPCases',
								component: SAPCaseListComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.SAP
								}
							},

							{
								path: 'SAPCases/:caseID',
								component: SAPCaseDetailComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.SAP
								}
							},


							/*
							 * ------------------------------------------------
							 * FLAGGED ASSETS
							 * ------------------------------------------------
							 */
							{
								path: 'flagged',
								component: OrganisationFlaggedAssetsComponent
							},


							/*
							 * ------------------------------------------------
							 * ENERGY
							 * ------------------------------------------------
							 */
							{
								path: 'energy-dashboard',
								component: EnergyDashboardComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Energy_Monitoring
								}
							},

							{
								path: 'utility-reporting',
								component: EnergyReportingComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Energy_Monitoring
								}
							},

							{
								path: 'utility-report-history',
								component: HistoricalReportsComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Energy_Monitoring
								}
							},


							/*
							 * ------------------------------------------------
							 * ASSETS
							 * ------------------------------------------------
							 */
							{
								path: 'assets',
								component: AssetListComponent
							},

							{
								path: 'asset/:assetID',
								component: AssetDetailsComponent
							},

							{
								path: 'compare',
								component: AssetComparisonComponent,
								canActivate: [FeatureGuard],
								data: {
									featureID: AccountFeatures.Compare_Assets
								}
							},


							/*
							 * ------------------------------------------------
							 * LOCATIONS
							 * ------------------------------------------------
							 */
							{
								path: 'location/:sublocation',
								component: HierarchyDashboardComponent,

								children: [
									{
										path: 'asset/:assetID',
										component: AssetDetailsComponent
									}
								]
							}
						]
					}
				]
			}
		]
	},


	/*
	 * ============================================================
	 * NON-AUTHENTICATED / SYSTEM ROUTES
	 * ============================================================
	 */
	{
		path: 'accountdisabled',
		component: AccountdisabledComponent
	},

	{
		path: 'unverified',
		component: UnverifiedEmailComponent
	},

	{
		path: 'unauthorised',
		component: UnauthorisedComponent
	},

	{
		path: 'error',
		component: ErrorComponent
	},

	{
		path: 'login',
		component: LoginComponent
	},

	{
		path: 'logout',
		component: LogoutComponent
	}
];


@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			scrollPositionRestoration: 'disabled'
		})
	],
	exports: [
		RouterModule
	]
})
export class AppRoutingModule { }